<template>
  <div class="fullscreen-modal" v-if="visible">
    <div class="modal-wrapper">
      <!-- Header -->
      <div class="modal-header-custom">
        <button type="button" class="btn-back" @click="close">
          <i class="bx bx-arrow-back"></i>
        </button>
        <span class="title">Registrar volume</span>
        <div class="spacer"></div>
      </div>

      <div class="modal-body-custom">
        <!-- Photo Capture Area -->
        <div class="photo-section" :class="{ 'has-photo': photo }">
          <div class="photo-area" @click="openCamera">
            <div class="photo-preview" v-if="photo">
              <img :src="photo" alt="Preview" />
              <button type="button" class="btn-remove-photo" @click.stop="removePhoto">
                <i class="bx bx-x"></i>
              </button>
            </div>
            <div class="photo-placeholder" v-else>
              <i class="bx bx-camera"></i>
              <span class="subtext">Obrigatorio</span>
            </div>
          </div>
          <button type="button" class="btn-capture" @click="openCamera" v-if="!photo">
            <i class="bx bx-camera"></i>
            Capturar foto
          </button>
        </div>

        <!-- Item Type -->
        <div class="section">
          <label class="section-title">Tipo</label>
          <div class="type-grid">
            <div
              v-for="type in itemTypes"
              :key="type.id"
              class="type-item"
              :class="{ selected: selectedType === type.id, 'type-outro': type.name && type.name.toLowerCase() === 'outro' }"
              @click="selectType(type.id)"
            >
              <i :class="'bx ' + type.icon"></i>
              <span>{{ type.name }}</span>
            </div>
          </div>
        </div>

        <!-- Item Name - Only shows when "Outro" is selected -->
        <div class="section" v-if="isOutroSelected">
          <label class="section-title">Nome <span class="required">*</span></label>
          <input
            type="text"
            class="name-input"
            v-model="itemName"
            placeholder="Digite o nome do item"
          />
        </div>

        <!-- Location -->
        <div class="section">
          <label class="section-title">Localizacao</label>
          <div class="location-grid">
            <div
              v-for="location in locations"
              :key="location.id"
              class="location-item"
              :class="{ selected: selectedLocation === location.id }"
              @click="selectLocation(location.id)"
            >
              <div class="location-check" v-if="selectedLocation === location.id">
                <i class="bx bx-check"></i>
              </div>
              <span class="location-name">{{ location.name }}</span>
              <span class="location-count">{{ location.items_count || 0 }} volumes alocados</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="modal-footer-custom">
        <button
          type="button"
          class="btn-register"
          :disabled="!canSubmit || loading"
          @click="submit"
        >
          <b-spinner small v-if="loading"></b-spinner>
          <span v-else>Registrar</span>
        </button>
        <button
          type="button"
          class="btn-print"
          :disabled="!canSubmit || loading"
          @click="printLabels"
        >
          <i class="bx bx-printer"></i>
          Registrar + Imprimir Etiqueta
        </button>
        <span class="print-note">2 vias serao impressas (participante + item)</span>
      </div>

      <!-- Camera Overlay -->
      <div class="camera-overlay" v-if="showCamera">
        <video ref="video" autoplay playsinline></video>
        <div class="camera-controls">
          <button type="button" class="btn-camera-cancel" @click="closeCamera">
            <i class="bx bx-x"></i>
          </button>
          <button type="button" class="btn-camera-capture" @click="capturePhoto">
            <i class="bx bx-camera"></i>
          </button>
        </div>
      </div>
      <canvas ref="canvas" style="display: none;"></canvas>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { STORAGE_MODULE } from '@/modules/storage'
import { successMessage, errorMessage } from '@/components/alertsMessages'
// import bluetoothPrinter from '@/modules/storage/service/bluetoothPrinter' // antigo (via app nativo)
import bluetoothPrinter from '@/modules/storage/service/webBluetoothPrinter'
import { enqueuePrintJob } from '@/modules/storage/service/storageService'

export default {
  name: 'register-modal',
  props: {
    participant: {
      type: Object,
      default: null
    },
    locations: {
      type: Array,
      default: function () { return [] }
    },
    itemTypes: {
      type: Array,
      default: function () { return [] }
    }
  },
  data: function () {
    return {
      visible: false,
      photo: null,
      selectedType: null,
      itemName: '',
      selectedLocation: null,
      loading: false,
      showCamera: false,
      stream: null
    }
  },
  computed: {
    selectedTypeObj: function () {
      var self = this
      if (!self.selectedType) return null
      return self.itemTypes.find(function (t) { return t.id === self.selectedType }) || null
    },
    isOutroSelected: function () {
      return this.selectedTypeObj && this.selectedTypeObj.name && this.selectedTypeObj.name.toLowerCase() === 'outro'
    },
    canSubmit: function () {
      if (this.isOutroSelected) {
        return this.photo && this.selectedType && this.selectedLocation && this.itemName.trim() !== ''
      }
      return this.photo && this.selectedType && this.selectedLocation
    },
    btConnected: function () {
      return bluetoothPrinter.isConnected()
    }
  },
  watch: {
    isOutroSelected: function (newVal) {
      // Clear itemName when changing from "outro" to another type
      if (!newVal) {
        this.itemName = ''
      }
    }
  },
  methods: {
    ...mapActions(STORAGE_MODULE, ['storeNewItem', 'reloadLocations']),
    open: function () {
      this.resetForm()
      this.visible = true
      document.body.style.overflow = 'hidden'
    },
    close: function () {
      this.closeCamera()
      this.visible = false
      document.body.style.overflow = ''
      this.$emit('close')
    },
    resetForm: function () {
      this.photo = null
      this.selectedType = null
      this.itemName = ''
      this.selectedLocation = null
      this.loading = false
    },
    selectType: function (typeId) {
      this.selectedType = typeId
    },
    selectLocation: function (locationId) {
      this.selectedLocation = locationId
    },
    openCamera: function () {
      var self = this
      self.showCamera = true
      self.$nextTick(function () {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: 'environment',
              width: { ideal: 640 },
              height: { ideal: 480 }
            }
          })
            .then(function (stream) {
              self.stream = stream
              self.$refs.video.srcObject = stream
            })
            .catch(function (error) {
              console.error('Erro ao acessar camera:', error)
              errorMessage('Nao foi possivel acessar a camera')
              self.showCamera = false
            })
        } else {
          errorMessage('Camera nao suportada neste dispositivo')
          self.showCamera = false
        }
      })
    },
    closeCamera: function () {
      if (this.stream) {
        this.stream.getTracks().forEach(function (track) {
          track.stop()
        })
        this.stream = null
      }
      this.showCamera = false
    },
    capturePhoto: function () {
      var video = this.$refs.video
      var canvas = this.$refs.canvas
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      var ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0)
      this.photo = canvas.toDataURL('image/jpeg', 0.8)
      this.closeCamera()
    },
    removePhoto: function () {
      this.photo = null
    },
    buildItemData: function () {
      var self = this
      var typeName = self.selectedTypeObj ? self.selectedTypeObj.name : null
      return {
        participant_id: self.participant.id,
        storage_location_id: self.selectedLocation,
        storage_item_type_id: self.selectedType,
        custom_type_name: self.isOutroSelected ? self.itemName : typeName,
        photo: self.photo,
        notes: null
      }
    },
    submit: function () {
      var self = this
      if (!self.canSubmit || !self.participant) return

      self.loading = true

      var data = self.buildItemData()

      self.storeNewItem(data)
        .then(function (result) {
          self.loading = false
          successMessage(result.message || 'Volume registrado com sucesso!')
          self.reloadLocations()
          self.$emit('registered', result.data)
        })
        .catch(function (error) {
          self.loading = false
          errorMessage(error.message || 'Erro ao registrar volume')
        })
    },
    formatDateTimePrint: function (dateStr) {
      if (!dateStr) {
        var now = new Date()
        var d = String(now.getDate()).padStart(2, '0')
        var m = String(now.getMonth() + 1).padStart(2, '0')
        var y = now.getFullYear()
        var h = String(now.getHours()).padStart(2, '0')
        var min = String(now.getMinutes()).padStart(2, '0')
        return d + '/' + m + '/' + y + ' ' + h + ':' + min
      }
      var date = new Date(dateStr)
      var dd = String(date.getDate()).padStart(2, '0')
      var mm = String(date.getMonth() + 1).padStart(2, '0')
      var yyyy = date.getFullYear()
      var hh = String(date.getHours()).padStart(2, '0')
      var mi = String(date.getMinutes()).padStart(2, '0')
      return dd + '/' + mm + '/' + yyyy + ' ' + hh + ':' + mi
    },
    ensureConnected: function () {
      var self = this
      if (bluetoothPrinter.isConnected()) {
        return Promise.resolve(true)
      }
      return bluetoothPrinter.connect()
        .then(function () {
          self.$forceUpdate()
          return true
        })
    },
    printLabels: function () {
      var self = this
      if (!self.canSubmit || !self.participant) return

      // O REGISTRO não depende da impressora. Antes o fluxo começava pelo
      // ensureConnected(), então num aparelho sem Bluetooth (iPhone, webview) a
      // conexão falhava, caía no catch e o volume nem chegava a ser gravado.
      self.loading = true

      self.storeNewItem(self.buildItemData())
        .then(function (result) {
          self.loading = false
          successMessage(result.message || 'Volume registrado com sucesso!')
          self.reloadLocations()
          self.$emit('registered', result.data)

          self.printItemLabels(result.data)
        })
        .catch(function (error) {
          self.loading = false
          errorMessage(error.message || 'Erro ao registrar volume')
        })
    },
    /**
     * Imprime as etiquetas do item pelo melhor caminho disponível: impressora
     * Bluetooth quando há uma conectada, senão a impressão do próprio navegador.
     *
     * O fallback pelo navegador existia até o app da chapelaria ser criado e foi
     * removido junto. Sem ele, quem opera pelo celular (onde o Web Bluetooth não
     * existe) ficou sem imprimir.
     */
    printItemLabels: function (item) {
      var self = this
      var typeName = item.type_name || item.custom_type_name || 'Item'

      // Sem impressora aqui: manda para a fila da estação, que tem a térmica
      // pareada. É o caminho do iPhone e de qualquer webview, onde o Web
      // Bluetooth não existe. Se a fila falhar, ainda resta a impressão do
      // navegador para o operador não ficar sem etiqueta.
      if (!bluetoothPrinter.isSupported() || !bluetoothPrinter.isConnected()) {
        var location = self.locations.find(function (l) { return l.id === item.storage_location_id })

        enqueuePrintJob({
          storage_item_id: item.id,
          type_name: typeName,
          participant_name: self.participant.name,
          location_name: location ? location.name : null,
          date_time: self.formatDateTimePrint(item.stored_at)
        })
          .then(function (res) {
            successMessage(res.message || 'Etiqueta enviada para a impressora da estação.')
          })
          .catch(function () {
            errorMessage('Não foi possível enviar para a estação. Abrindo a impressão do navegador.')
            self.openPrintWindow(item)
          })
        return
      }

      bluetoothPrinter.printStorageLabel({
        code: item.code,
        typeName: typeName,
        participantName: self.participant.name,
        dateTime: self.formatDateTimePrint(item.stored_at)
      })
        .then(function () {
          successMessage('Etiquetas impressas com sucesso!')
        })
        .catch(function (err) {
          errorMessage('Erro na impressão: ' + (err.message || '') + ' Abrindo a impressão do navegador.')
          self.openPrintWindow(item)
        })
    },
    /**
     * Impressão pelo navegador: monta as duas etiquetas (50mm x 30mm) num iframe
     * oculto e chama o diálogo de impressão do aparelho.
     */
    openPrintWindow: function (item) {
      var self = this
      var location = self.locations.find(function (l) { return l.id === item.storage_location_id })
      var typeName = item.type_name || item.custom_type_name || 'Item'
      var dateTime = self.formatDateTimePrint(item.stored_at)

      var printContent = '<!DOCTYPE html><html><head><title>Etiquetas - ' + item.code + '</title>' +
        '<style>@page { size: 50mm 30mm; margin: 0; } body { margin: 0; padding: 0; font-family: Arial, sans-serif; } ' +
        '.label { width: 50mm; height: 30mm; padding: 2mm; box-sizing: border-box; page-break-after: always; border: 1px dashed #ccc; } ' +
        '.label:last-child { page-break-after: auto; } .code { font-size: 14pt; font-weight: bold; text-align: center; margin-bottom: 1mm; } ' +
        '.type { font-size: 8pt; text-align: center; margin-bottom: 1mm; } .location { font-size: 7pt; text-align: center; margin-bottom: 1mm; } ' +
        '.participant { font-size: 7pt; text-align: center; color: #666; } .date { font-size: 6pt; text-align: center; color: #999; margin-top: 1mm; }' +
        '</style></head><body>' +
        '<div class="label"><div class="code">' + item.code + '</div><div class="type">' + typeName + '</div>' +
        '<div class="location">' + (location ? location.name : '') + '</div><div class="participant">' + self.participant.name + '</div>' +
        '<div class="date">' + dateTime + '</div></div>' +
        '<div class="label"><div class="code">' + item.code + '</div><div class="type">' + typeName + '</div>' +
        '<div class="location">' + (location ? location.name : '') + '</div><div class="participant">' + self.participant.name + '</div>' +
        '<div class="date">' + dateTime + '</div></div>' +
        '</body></html>'

      var existingFrame = document.getElementById('storage-print-frame')
      if (existingFrame) existingFrame.remove()

      var iframe = document.createElement('iframe')
      iframe.id = 'storage-print-frame'
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = 'none'
      document.body.appendChild(iframe)

      var doc = iframe.contentDocument || iframe.contentWindow.document
      doc.open()
      doc.write(printContent)
      doc.close()

      setTimeout(function () {
        iframe.contentWindow.focus()
        iframe.contentWindow.print()
        setTimeout(function () { iframe.remove() }, 1000)
      }, 250)
    }
  },
  beforeDestroy: function () {
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
.fullscreen-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  z-index: 1051;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 500px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  overflow: hidden;
}

.modal-header-custom {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;

  .btn-back {
    background: none;
    border: none;
    font-size: 22px;
    color: #666;
    cursor: pointer;
    padding: 0;
    width: 32px;

    &:hover {
      color: #333;
    }
  }

  .title {
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
  }

  .spacer {
    width: 32px;
  }
}

.modal-body-custom {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.section {
  margin: 0;
  padding: 10px 0;

  .section-title {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin: 0 0 6px 0;
    padding: 0;

    .required {
      color: #dc3545;
    }
  }
}

.photo-section {
  margin: 0;
  padding: 10px 0;

  .photo-area {
    width: 100%;
    height: 120px;
    border: 2px dashed #d9d9d9;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    margin-bottom: 8px;
    overflow: hidden;
    background: #fafafa;

    &:hover {
      border-color: #556ee6;
    }

    .photo-preview {
      position: relative;
      width: 100%;
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .btn-remove-photo {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        border: none;
        background: rgba(255, 255, 255, 0.95);
        color: #dc3545;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);

        i {
          font-size: 18px;
        }
      }
    }

    .photo-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;

      i {
        font-size: 32px;
        color: #ccc;
        margin-bottom: 4px;
      }

      .subtext {
        font-size: 11px;
        color: #999;
      }
    }
  }

  // When photo is captured, show larger preview
  &.has-photo .photo-area {
    height: 220px;
    border: none;
    border-radius: 12px;
    margin-bottom: 0;
  }

  .btn-capture {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: #556ee6;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
      background: #4a5fd4;
    }

    i {
      font-size: 16px;
    }
  }
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin: 0;
  padding: 0;

  .type-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 4px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #556ee6;
    }

    &.selected {
      border-color: #556ee6;
      background: #f0f4ff;
    }

    &.type-outro {
      grid-column: 1 / -1;
      flex-direction: row;
      justify-content: center;
      gap: 6px;
      padding: 10px;
    }

    i {
      font-size: 20px;
      color: #666;
      margin-bottom: 4px;
    }

    &.type-outro i {
      margin-bottom: 0;
    }

    span {
      font-size: 10px;
      color: #333;
      text-align: center;
    }

    &.type-outro span {
      font-size: 12px;
    }
  }
}

.name-input {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  margin: 0;

  &:focus {
    outline: none;
    border-color: #556ee6;
  }

  &::placeholder {
    color: #999;
  }
}

.location-grid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;

  .location-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #556ee6;
    }

    &.selected {
      border-color: #556ee6;
      background: #f0f4ff;
    }

    .location-check {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background: #556ee6;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      i {
        color: #fff;
        font-size: 12px;
      }
    }

    .location-name {
      flex: 1;
      font-size: 12px;
      font-weight: 500;
      color: #333;
    }

    .location-count {
      font-size: 10px;
      color: #999;
    }
  }
}

.modal-footer-custom {
  padding: 12px;
  border-top: 1px solid #f0f0f0;
  flex-shrink: 0;

  .btn-register {
    width: 100%;
    background: #556ee6;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 8px;

    &:hover:not(:disabled) {
      background: #4a5fd4;
    }

    &:disabled {
      background: #a8b8f0;
      cursor: not-allowed;
    }
  }

  .btn-print {
    width: 100%;
    background: #74788d;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    margin-bottom: 6px;

    &:hover:not(:disabled) {
      background: #5d6071;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .print-note {
    display: block;
    text-align: center;
    font-size: 10px;
    color: #999;
  }
}

.camera-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #000;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  video {
    max-width: 100%;
    max-height: calc(100% - 100px);
    border-radius: 12px;
  }

  .camera-controls {
    display: flex;
    gap: 20px;
    margin-top: 20px;

    .btn-camera-cancel, .btn-camera-capture {
      width: 50px;
      height: 50px;
      border-radius: 50%;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      i {
        font-size: 22px;
      }
    }

    .btn-camera-cancel {
      background: rgba(255,255,255,0.3);
      color: #fff;
    }

    .btn-camera-capture {
      background: #fff;
      color: #333;
    }
  }
}
</style>
